export interface WorkosUserLike {
  id: string
  email: string
  profilePictureUrl: string | null
  firstName: string | null
  lastName: string | null
}

export interface WorkosClientAuthLike {
  loading: boolean
  organizationId?: string
  role?: string
  roles?: string[]
  user: WorkosUserLike | null
  signOut: (options?: { returnTo?: string }) => Promise<void>
  switchToOrganization: (
    organizationId: string,
    options?: {
      returnTo?: string
      revalidationStrategy?: "none" | "tag" | "path"
    }
  ) => Promise<unknown>
}

interface WorkosMembershipApiRecord {
  id: string
  userId: string
  role: string
  userName?: string | null
  userEmail?: string | null
  userAvatarUrl?: string | null
}

interface WorkosInvitationApiRecord {
  id: string
  email: string
  role: string
  status: "pending" | "accepted" | "expired" | "revoked"
}

async function fetchWorkosOrganizationApi<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  })

  const payload = (await response.json()) as T | { error?: string }

  if (!response.ok) {
    throw new Error(
      typeof payload === "object" && payload !== null && "error" in payload && typeof payload.error === "string"
        ? payload.error
        : "Failed to access WorkOS organization API"
    )
  }

  return payload as T
}

function createWorkosMembership(membership: WorkosMembershipApiRecord) {
  return {
    id: membership.id,
    role: membership.role,
    publicUserData: {
      identifier: membership.userEmail || membership.userId,
      firstName: membership.userName || "",
      lastName: "",
      imageUrl: membership.userAvatarUrl ?? null,
    },
    update: async ({ role }: { role: string }) =>
      fetchWorkosOrganizationApi(`/api/auth/organizations/active/memberships/${membership.id}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      }),
    destroy: async () =>
      fetchWorkosOrganizationApi(`/api/auth/organizations/active/memberships/${membership.id}`, {
        method: "DELETE",
      }),
  }
}

function createWorkosInvitation(invitation: WorkosInvitationApiRecord) {
  return {
    id: invitation.id,
    emailAddress: invitation.email,
    role: invitation.role,
    status: invitation.status,
    revoke: async () =>
      fetchWorkosOrganizationApi(`/api/auth/organizations/active/invitations/${invitation.id}`, {
        method: "DELETE",
      }),
  }
}

function createWorkosOrganization(organizationId: string) {
  return {
    id: organizationId,
    getMemberships: async () => {
      const payload = await fetchWorkosOrganizationApi<{
        memberships: WorkosMembershipApiRecord[]
      }>("/api/auth/organizations/active/memberships")

      return {
        data: payload.memberships.map(createWorkosMembership),
      }
    },
    getInvitations: async () => {
      const payload = await fetchWorkosOrganizationApi<{
        invitations: WorkosInvitationApiRecord[]
      }>("/api/auth/organizations/active/invitations")

      return {
        data: payload.invitations.map(createWorkosInvitation),
      }
    },
    inviteMember: async ({
      emailAddress,
      role,
    }: {
      emailAddress: string
      role: string
    }) => {
      const payload = await fetchWorkosOrganizationApi<{
        invitation: WorkosInvitationApiRecord
      }>("/api/auth/organizations/active/invitations", {
        method: "POST",
        body: JSON.stringify({
          email: emailAddress,
          role,
        }),
      })

      return createWorkosInvitation(payload.invitation)
    },
  }
}

export function mapWorkosUserToAuthUser(user: WorkosUserLike | null) {
  if (!user) {
    return null
  }

  const emailId = `${user.id}:primary-email`
  const fullName = [user.lastName, user.firstName].filter(Boolean).join(" ").trim()

  return {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    fullName: fullName || user.email,
    imageUrl: user.profilePictureUrl,
    hasImage: Boolean(user.profilePictureUrl),
    username: user.email.split("@")[0] ?? user.email,
    primaryEmailAddressId: emailId,
    primaryEmailAddress: {
      id: emailId,
      emailAddress: user.email,
    },
    emailAddresses: [
      {
        id: emailId,
        emailAddress: user.email,
      },
    ],
    organizationMemberships: [],
    reload: async () => {},
  }
}

export function buildWorkosHostedAuthPath(screenHint: "sign-in" | "sign-up", returnPath?: string | null) {
  const params = new URLSearchParams()

  if (returnPath?.startsWith("/") && !returnPath.startsWith("//")) {
    params.set("returnPath", returnPath)
  }

  const query = params.toString()

  return `/api/auth/${screenHint}${query ? `?${query}` : ""}`
}

export function mapWorkosAuthToAuthUserState(auth: WorkosClientAuthLike) {
  const user = mapWorkosUserToAuthUser(auth.user)

  return {
    isLoaded: !auth.loading,
    isSignedIn: Boolean(user),
    user,
  }
}

/** @deprecated Use mapWorkosAuthToAuthUserState */
export const mapWorkosAuthToUserState = mapWorkosAuthToAuthUserState

export function mapWorkosAuthToAuthOrganizationState(auth: WorkosClientAuthLike) {
  const membershipRole = auth.role ?? auth.roles?.[0] ?? null

  return {
    isLoaded: !auth.loading,
    organization: auth.organizationId ? createWorkosOrganization(auth.organizationId) : null,
    membership: membershipRole
      ? {
          role: membershipRole,
        }
      : null,
  }
}

/** @deprecated Use mapWorkosAuthToAuthOrganizationState */
export const mapWorkosAuthToOrganizationState = mapWorkosAuthToAuthOrganizationState

export function createWorkosAuthClient(auth: WorkosClientAuthLike) {
  return {
    signOut: async (options?: { returnTo?: string }) => {
      const location = (globalThis as { location?: { assign(url: string): void } }).location

      if (!location) {
        await auth.signOut(options)
        return
      }

      const returnPath =
        options?.returnTo?.startsWith("/") && !options.returnTo.startsWith("//") ? options.returnTo : undefined

      location.assign(returnPath ? `/api/auth/logout?returnTo=${encodeURIComponent(returnPath)}` : "/api/auth/logout")

      return new Promise<void>(() => {})
    },
    handleRedirectCallback: async () => {},
    createOrganization: async () => {
      throw new Error("AUTH_PROVIDER=workos does not support createOrganization through the client facade yet")
    },
    setActive: async ({ organization }: { organization?: string | null }) => {
      if (!organization) {
        return
      }

      await auth.switchToOrganization(organization, {
        revalidationStrategy: "none",
      })
    },
  }
}

export function redirectToWorkosHostedAuth(
  screenHint: "sign-in" | "sign-up",
  returnPath?: string | null
): Promise<never> {
  const location = (globalThis as { location?: { assign(url: string): void } }).location

  if (!location) {
    throw new Error("WorkOS hosted auth redirects are only available in the browser")
  }

  location.assign(buildWorkosHostedAuthPath(screenHint, returnPath))

  return new Promise<never>(() => {})
}
